// Corner stats for the cockpit Penumbra chart — favourite (highest
// price share) and outsider (lowest price share among NAMED outcomes,
// excluding The Field). Price-derived only; no vibe dependency, so
// identical on Arena (single-ring) and Live.
//
// Architect-approved 2026-05-20 (Penumbra Part C). Brief at
// docs/audit/2026-05-20-penumbra-corners-brief.md. The Field exclusion
// from the outsider calc is a correctness point, not cosmetic — The
// Field is the catch-all "something not listed" outcome, so a low
// Field price means "few expect a surprise," not "here's an
// underpriced specific long-shot." Naming The Field as the outsider
// would mislead pledgers about where a contrarian-correct call pays
// off. (See public/about.html "How it works" §The Field paragraph.)
//
// Follows the lib/feedSort.ts house pattern: a small typed pure
// helper with a minimal input interface, called from the mounting
// rather than embedded inline in the JSX.

export interface CornerOutcome {
  label:    string;
  price:    number;   // 0..1 share
  color:    string;
  isField?: boolean;  // The Field catch-all — excluded from outsider
}

export interface CornerStat {
  label: string;   // the outcome's display label
  pct:   number;   // 0..100, rounded for display
  color: string;
}

export interface PenumbraCorners {
  favourite: CornerStat | null;
  outsider:  CornerStat | null;
}

export function computePenumbraCorners(
  outcomes: readonly CornerOutcome[],
): PenumbraCorners {
  if (outcomes.length === 0) return { favourite: null, outsider: null };

  // Defensive: sum positive prices and guard against a zero total
  // (shouldn't happen for a real market, but keeps the helper pure
  // and safe for any input shape).
  const total = outcomes.reduce((s, o) => s + (o.price > 0 ? o.price : 0), 0) || 1;
  const pct = (p: number): number => Math.round((p / total) * 100);

  // Favourite — highest price; all outcomes eligible (including The
  // Field if it happens to be highest, which is unusual but valid).
  const fav = outcomes.reduce((best, o) => (o.price > best.price ? o : best));

  // Outsider — lowest price among NAMED outcomes (The Field excluded).
  // If after exclusion there are fewer than 2 named outcomes, suppress
  // the outsider — nothing meaningful to show. (The favourite has
  // already been picked from the full set, so a 2-outcome market with
  // one Field + one named will show favourite-only.)
  const named = outcomes.filter(o => !o.isField);
  const out = named.length >= 2
    ? named.reduce((low, o) => (o.price < low.price ? o : low))
    : null;

  return {
    favourite: { label: fav.label, pct: pct(fav.price), color: fav.color },
    outsider:  out ? { label: out.label, pct: pct(out.price), color: out.color } : null,
  };
}
